// src/modules/wiring/hooks/useWireKeyboardMovement.js
//
// H5 Aşama 2: useComponentWiring'den çıkarıldı.
// "AWSD" tuşları ile aktif vertex'i snap noktasından küçük offset miktarı
// kadar (base.movementOffset) hareket ettirir. Snap state'i (x: 0/1/2, y: 0/1/2)
// 3-konum döngü mantığı ile yönetilir (önce offset, sonra tam snap, sonra ters offset).
//
// Kullanım: useBaseWiring sonrası çağırılır; window keydown listener'ı kurar.

import { useEffect } from 'react';
import * as THREE from 'three';

/**
 * @param {object} args
 * @param {object} args.base - useBaseWiring API (snapMarkerRef, verticesRef, snapState,
 *   movementOffset, pushOrUpdateVertex, updateTempLine)
 */
export function useWireKeyboardMovement({ base }) {
  useEffect(() => {
    if (!base) return;

    const handleKeyDown = (e) => {
      if (!['a', 'w', 's', 'd'].includes(e.key)) return;
      if (!base.snapMarkerRef.current) return;

      const snapPos = base.snapMarkerRef.current.position;
      const previousPos = base.verticesRef.current[base.verticesRef.current.length - 1];
      if (!previousPos) return;

      const newPos = new THREE.Vector3(previousPos.x, previousPos.y, 0);

      const isSnapRight = snapPos.x >= previousPos.x;
      const isSnapLeft = snapPos.x <= previousPos.x;
      const isSnapUp = snapPos.y >= previousPos.y;
      const isSnapDown = snapPos.y <= previousPos.y;

      if (e.key === 'a' && isSnapLeft) {
        base.snapState.current.y = 0;
        if (!base.snapState.current.x) {
          newPos.x = snapPos.x + base.movementOffset;
          base.snapState.current.x = 1;
        } else if (base.snapState.current.x === 1) {
          newPos.x = snapPos.x;
          base.snapState.current.x = 2;
        } else if (base.snapState.current.x === 2) {
          newPos.x = snapPos.x - base.movementOffset;
          base.snapState.current.x = 0;
        }
      } else if (e.key === 'd' && isSnapRight) {
        base.snapState.current.y = 0;
        if (!base.snapState.current.x) {
          newPos.x = snapPos.x - base.movementOffset;
          base.snapState.current.x = 1;
        } else if (base.snapState.current.x === 1) {
          newPos.x = snapPos.x;
          base.snapState.current.x = 2;
        } else if (base.snapState.current.x === 2) {
          newPos.x = snapPos.x + base.movementOffset;
          base.snapState.current.x = 0;
        }
      } else if (e.key === 'w' && isSnapUp) {
        base.snapState.current.x = 0;
        if (!base.snapState.current.y) {
          newPos.y = snapPos.y - base.movementOffset;
          base.snapState.current.y = 1;
        } else if (base.snapState.current.y === 1) {
          newPos.y = snapPos.y;
          base.snapState.current.y = 2;
        } else if (base.snapState.current.y === 2) {
          newPos.y = snapPos.y + base.movementOffset;
          base.snapState.current.y = 0;
        }
      } else if (e.key === 's' && isSnapDown) {
        base.snapState.current.x = 0;
        if (!base.snapState.current.y) {
          newPos.y = snapPos.y + base.movementOffset;
          base.snapState.current.y = 1;
        } else if (base.snapState.current.y === 1) {
          newPos.y = snapPos.y;
          base.snapState.current.y = 2;
        } else if (base.snapState.current.y === 2) {
          newPos.y = snapPos.y - base.movementOffset;
          base.snapState.current.y = 0;
        }
      }

      // newPos !== previousPos kontrolü: Vector3 referans karşılaştırması her
      // zaman true (yeni instance), ama orijinal kodu birebir koruyoruz
      if (newPos !== previousPos) {
        base.pushOrUpdateVertex(newPos);
        base.updateTempLine();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [base]);
}

export default useWireKeyboardMovement;
